import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { UserService } from 'src/app/api/services';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from '../../constant/messages';
import { APP_CONSTANTS } from '../../constant/constants';
import { Profile } from 'src/app/shared/model/user';
import { heroPencil } from '@ng-icons/heroicons/outline';
import { ImageUploadModalComponent } from 'src/app/shared/component/image-upload-modal/image-upload-modal.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';
import { finalize } from 'rxjs';
import {
  featherAward,
  featherExternalLink,
  featherUser,
} from '@ng-icons/feather-icons';

@Component({
  selector: 'app-profile-layout',
  imports: [
    RouterModule,
    NgIcon,
    ImageUploadModalComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    LoadingButtonComponent,
  ],
  templateUrl: './profile-layout.component.html',
  viewProviders: [
    provideIcons({
      featherUser,
      featherExternalLink,
      featherAward,
      heroPencil,
    }),
  ],
})
export class ProfileLayoutComponent implements OnInit {
  profile: Profile = {} as Profile;

  isLoading: boolean = false;
  isUpdatingFile: boolean = false;
  showModalUpdate: boolean = false;
  modalLoading: boolean = false;
  editingFile: boolean = false;

  personalFileUrl: string = '';

  constructor(
    private tokenStorage: TokenStorageService,
    private userService: UserService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadProfile();
  }

  private loadProfile(): void {
    const currentUser = this.tokenStorage.getAuth();

    if (!currentUser?.user?.idUser) {
      this.toastService.showMessages('error', [MESSAGES.AUTH.FORBIDDEN]);
      return;
    }

    this.isLoading = true;
    this.userService
      .getProfile(currentUser.user.idUser)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => (this.profile = data.dto.user),
        error: () =>
          this.toastService.showMessages('error', [
            MESSAGES.USER.PROFILE_LOAD_ERROR,
          ]),
      });
  }

  toggleEditFile(): void {
    this.editingFile = !this.editingFile;
    this.personalFileUrl = this.profile?.urlPersonalFile || '';
  }

  savePersonalFile(): void {
    if (!this.profile?.idUser) return;

    if (!this.isValidUrl(this.personalFileUrl)) {
      this.toastService.showMessages('warning', [
        'Ingrese una URL válida que empiece con http:// o https://',
      ]);
      return;
    }

    this.isUpdatingFile = true;
    this.userService
      .updatePersonalFile(this.profile.idUser, this.personalFileUrl)
      .pipe(finalize(() => (this.isUpdatingFile = false)))
      .subscribe({
        next: () => {
          this.profile.urlPersonalFile = this.personalFileUrl;
          this.editingFile = false;
          this.toastService.showMessages('success', [
            MESSAGES.USER.UPDATE_SUCCESS,
          ]);
        },
        error: () =>
          this.toastService.showMessages('error', [MESSAGES.USER.UPDATE_ERROR]),
      });
  }

  private isValidUrl(url: string): boolean {
    const urlPattern = /^(https?:\/\/)[\w.-]+(\.[\w\.-]+)+[/#?]?.*$/;
    return urlPattern.test(url);
  }

  openModalUploadImage() {
    this.showModalUpdate = true;
  }

  onCloseModalUploadImage() {
    this.showModalUpdate = false;
  }

  uploadLogo(file: File): void {
    if (!file || !this.profile?.idUser) return;

    this.modalLoading = true;

    this.userService
      .updateUrlAvatar(file)
      .pipe(
        finalize(() => {
          this.modalLoading = false;
          this.showModalUpdate = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.profile.urlAvatar = res.dto.url;
          this.toastService.showMessages('success', [
            MESSAGES.USER.UPDATE_IMAGE_PROFILE_SUCCESS,
          ]);
        },
        error: (err) => {
          const msgs = err.error?.listMessage || [
            MESSAGES.USER.UPDATE_IMAGE_PROFILE_ERROR,
          ];
          this.toastService.showMessages('error', msgs);
        },
      });
  }

  get userImage(): string {
    const url = this.profile?.urlAvatar;
    return url?.trim() ? url : APP_CONSTANTS.DEFAULT_IMAGE_PROFILE;
  }
}
